<template>
  <AppContainer max-width="max-w-3xl">
    <form
      autocomplete="off"
      @submit.prevent="submitForm"
    >
      <AppCard>
        <TextInput
          v-model.trim="formData.name"
          label="Name"
          :class="{ 'input-error': hasValidationError('name') }"
          :validation-error="getValidationError('name') || null"
          required
        />

        <ImageFileInput
          class="max-w-md"
          label="Logo"
          :validation-error="getValidationError('logo')"
          :preview="model?.logo"
          :deletable="!!model?.logo"
          @change="logo = $event.target.files[0] || null"
          @delete="deleteLogo"
        />

        <template #buttons>
          <ButtonSubmitRow
            :processing="isSubmitting"
            :cancel-route="{ name: 'app_province_list' }"
          />
        </template>
      </AppCard>
    </form>
  </AppContainer>
</template>

<script setup>
import { reactive, ref } from 'vue';
import { HttpStatusCode } from 'axios';
import { useRouter } from 'vue-router';

import { AppContainer } from '@/components/app';
import { AppCard } from '@/components/cards';
import { ButtonSubmitRow } from '@/components/buttons';
import { TextInput, ImageFileInput } from '@/components/form-inputs';

import { useFormSubmit, useHttp, useToasts } from '@/composables';

const props = defineProps({
  model: {
    type: Object,
    default: () => null,
  },
});

const { isSubmitting, submit, hasValidationError, getValidationError } = useFormSubmit();
const router = useRouter();
const http = useHttp();
const toasts = useToasts();

const formData = reactive({
  name: props.model?.name || null,
});

const logo = ref(null);

const submitForm = async () => {
  // Submit the form data
  const response = await submit(props.model?.id, formData, null, 'provinces');

  if (response.statusCode === HttpStatusCode.Ok) {
    const provinceId = response.data.id;

    // If a new logo was selected, upload it to the logo endpoint
    if (logo.value) {
      isSubmitting.value = true;
      const logoResponse = await uploadLogo(provinceId);

      if (logoResponse.statusCode !== HttpStatusCode.Ok) {
        isSubmitting.value = false;
        return;
      }
    }

    await router.push({ name: 'app_province_list' });
  }
};

const uploadLogo = async (provinceId) => {
  const data = new FormData();
  data.set('logo', logo.value);
  return await http.post(`/provinces/${provinceId}/logo`, data);
};

const deleteLogo = async () => {
  if (!props.model?.id) return;

  const response = await http.delete(`/provinces/${props.model.id}/logo`);

  if (response.statusCode === HttpStatusCode.Ok) {
    toasts.success('Logo deleted successfully');
    if (props.model) props.model.logo = null;
  } else {
    toasts.error('Could not delete logo');
  }
};
</script>
